import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { Card } from '../ui/card';

export const OvertimePerDiemArticle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      question: "What overtime rules apply in construction?",
      answer: "Construction workers encounter daily OT after 8 hours, weekly OT after 40, double time, weekend premiums, shift differentials, and union-specific rules. These vary by project, location, and contract."
    },
    {
      question: "How is per diem calculated in construction?",
      answer: "Per diem varies by company and includes factors like taxed vs untaxed amounts, full vs partial days, travel-in/out rules, location adjustments, and company-specific policies."
    },
    {
      question: "Why is tracking overtime and per diem important?",
      answer: "Accurate tracking ensures you're paid correctly, helps catch errors early, provides documentation for disputes, and gives you financial clarity before payday."
    },
    {
      question: "Can PayFlow track complex overtime rules?",
      answer: "Yes. PayFlow is designed specifically for construction and refinery work, supporting daily OT, weekly OT, double time, multiple rates, and per diem calculations in real time."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How Overtime and Per Diem Actually Work in Construction and Shutdowns",
    "description": "An in-depth explanation of overtime and per diem rules for construction and refinery workers, and how PayFlow helps track both in real time.",
    "author": {
      "@type": "Organization",
      "name": "ProjectFlow Automations"
    },
    "keywords": "construction overtime, per diem rules, shutdown OT, refinery payroll, PayFlow app",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://projectflowautomations.com/knowledge/overtime-perdiem"
    }
  };

  return (
    <>
      <Helmet>
        <title>Understanding Overtime & Per Diem in Construction | PayFlow</title>
        <meta 
          name="description" 
          content="See why overtime and per diem cause confusion in construction and shutdowns, and how PayFlow tracks both in real time to predict weekly pay before payday." 
        />
        <meta name="keywords" content="construction overtime rules, per diem construction, refinery per diem, OT after 8 app, shutdown overtime calculator, PayFlow paycheck estimate" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1115] pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="/knowledge"
            className="inline-flex items-center text-[#30D5C8] hover:text-[#30D5C8]/80 font-['Open_Sans'] mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Knowledge Center
          </a>

          <div className="mb-12">
            <div className="inline-block bg-[#30D5C8]/10 border border-[#30D5C8]/30 rounded-full px-4 py-2 mb-6">
              <span className="text-[#30D5C8] text-sm font-['Montserrat'] font-semibold">
                OVERTIME & PER DIEM
              </span>
            </div>
            <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-6 leading-tight">
              How Overtime and Per Diem Actually Work in Construction and Shutdowns
            </h1>
            <p className="text-[#CBD3D9] text-xl font-['Open_Sans'] leading-relaxed">
              Overtime and per diem create some of the biggest paycheck challenges for tradespeople — not because workers misunderstand them, but because every job handles them differently.
            </p>
          </div>

          <article className="space-y-8">
            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                Overtime: Not One-Size-Fits-All
              </h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                  Construction and refinery workers encounter unique overtime rules such as:
                </p>
                <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg">
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Daily OT after 8 hours</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Weekly OT after 40</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Double time rules</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Weekend premiums</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Shift differentials</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Union vs. non-union rules</span>
                  </li>
                </ul>
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mt-6">
                  On shutdowns, OT can change daily depending on the shift or job code.
                </p>
                <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold text-lg mt-4">
                  Workers rarely get real-time updates on how their overtime is being calculated — leading to surprises on payday.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                Per Diem: A Major Part of Worker Income
              </h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                  Per diem varies widely:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Taxed vs. untaxed</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Different tax treatment affects take-home pay</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Full vs. partial days</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Amounts vary based on hours worked</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Travel-in and travel-out</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Special rules for mobilization days</p>
                  </div>
                  <div className="bg-[#0D1115]/50 rounded-lg p-4">
                    <p className="text-[#30D5C8] font-['Montserrat'] font-semibold mb-2">Location adjustments</p>
                    <p className="text-[#CBD3D9] font-['Open_Sans'] text-sm">Rates change based on work location</p>
                  </div>
                </div>
                <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold text-lg mt-6">
                  A single missed per diem day can drastically change a worker's check.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-3xl text-[#30D5C8] mb-6">
                Why Real-Time Pay Tracking Matters
              </h2>
              <Card className="bg-[#1A2733]/50 backdrop-blur-sm border-[#30D5C8]/30 p-8">
                <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg mb-6">
                  Workers deserve to know:
                </p>
                <ul className="space-y-3 text-[#CBD3D9] font-['Open_Sans'] text-lg">
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">✓</span>
                    <span>Whether OT is stacking correctly</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">✓</span>
                    <span>Whether per diem was applied</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">✓</span>
                    <span>What their weekly total looks like</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">✓</span>
                    <span>If something is missing — before payday</span>
                  </li>
                </ul>
                <p className="text-[#F5F7F8] font-['Montserrat'] font-bold text-xl mt-6">
                  PayFlow was designed to make this simple and transparent.
                </p>
              </Card>
            </section>

            <Card className="bg-gradient-to-br from-[#30D5C8]/20 to-[#30D5C8]/5 border-[#30D5C8]/50 p-8">
              <p className="text-[#F5F7F8] font-['Open_Sans'] text-lg leading-relaxed mb-6">
                PayFlow by ProjectFlow Automations is built for construction and refinery workers who want clarity on hours, overtime, and per diem before payday.
              </p>
              <a
                href="/payflow"
                className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors"
              >
                Learn More About PayFlow
              </a>
            </Card>
          </article>

          <section className="mt-16">
            <h2 className="font-['Montserrat'] font-bold text-3xl text-[#F5F7F8] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="bg-[#1A2733]/50 backdrop-blur-sm border-[#506273]/30 p-6">
                  <h3 className="font-['Montserrat'] font-bold text-lg text-[#30D5C8] mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-[#CBD3D9] font-['Open_Sans'] leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};